/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { TextInput } from '@carbon/react';
import cn from 'classnames';
import _ from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { HexColorPicker } from 'react-colorful';
import { useIntl } from 'react-intl';
import { useDebounce, useClickAway, useKey } from 'react-use';

import { COLOR_REGEX } from '../../../util/validation/constants';
import { StyleTab as messages } from '../messages';

import './ColorPicker.scss';

interface ColorPickerProps {
  /**
   *
   */
  id: string;
  /**
   *
   */
  value: string;
  /**
   *
   */
  onChange: (value: string) => void;
  /**
   *
   */
  helperText?: string;
  /**
   *
   */
  labelText?: string;
  /**
   *
   */
  placeholder?: string;
  /**
   *
   */
  error?: string | null;
  /**
   *
   */
  readOnly: boolean;
}
export const ColorPicker = ({
  id,
  value,
  onChange,
  helperText = '',
  labelText = '',
  placeholder = '#FFFFFF',
  error = null,
  readOnly
}: ColorPickerProps) => {
  const { formatMessage: t } = useIntl();
  const pickerRef = useRef();

  const [realTimeColor, setRealTimeColor] = useState<string>(value);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  // Handle click outside color picker or not
  useClickAway(pickerRef, () => setIsOpen(false));

  // Focusing on another element closes the picker
  useKey(
    e => ['Tab', 'Enter'].includes(e.key),
    () => {
      if (
        !_.isNil(pickerRef.current) &&
        !(pickerRef.current as any).contains(document.activeElement)
      ) {
        setIsOpen(false);
      }
    },
    { event: 'keyup' }
  );

  // Escape will close the picker
  useKey('Escape', () => setIsOpen(false));

  useDebounce(
    () => {
      onChange(realTimeColor);
    },
    200,
    [realTimeColor]
  );

  useEffect(() => {
    setRealTimeColor(value);
  }, [value]);

  const openColorPicker = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleTextChange = useCallback(c => {
    setRealTimeColor(c.target.value);
  }, []);

  const handlePickerChange = useCallback(c => {
    setRealTimeColor(c);
  }, []);

  return (
    <div className="color-picker">
      <div className="color-picker__text">
        <TextInput
          pattern={COLOR_REGEX.toString()}
          helperText={helperText}
          id={`${id}-text`}
          data-testid={`${id}-text`}
          invalidText={error}
          invalid={!!error}
          labelText={labelText}
          placeholder={placeholder}
          value={realTimeColor}
          onChange={handleTextChange}
          readOnly={readOnly}
        />
      </div>
      <div className="color-picker__button" ref={pickerRef}>
        <button
          disabled={readOnly}
          id={`${id}-button`}
          aria-label={t(messages.colorPickerLabel)}
          style={{ backgroundColor: realTimeColor || placeholder }}
          onClick={openColorPicker}
        />
        <div
          className={cn({
            'color-picker__picker': true,
            'color-picker__picker--open': isOpen
          })}>
          <HexColorPicker
            data-testid={id}
            color={realTimeColor || placeholder}
            onChange={handlePickerChange}
          />
        </div>
      </div>
    </div>
  );
};
